import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import { apiErrorMessage, detailToString } from '../utils/apiError';

const API_BASE = config.API_BASE_URL;

// Website quote-request triage inbox. Anonymous prospects submit the
// "Request a quote" form on kancraftmfg.com; jobq-api persists them as
// quote_requests rows with drawing files in S3. This page is where Jesse
// reviews them: read the request, download the drawings, set status, and
// convert a real one into a customer + Draft quote (which then lives in
// the normal Quotes UI). Slack pings deep-link here as /quote-requests?id=N.

const STATUS_META = {
  new: { label: 'New', color: 'bg-warning text-dark' },
  in_review: { label: 'In Review', color: 'bg-info text-dark' },
  converted: { label: 'Converted', color: 'bg-success' },
  declined: { label: 'Declined', color: 'bg-secondary' },
  spam: { label: 'Spam', color: 'bg-dark' },
};

const FILTERS = [
  { key: 'active', label: 'All active' },   // everything except spam
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In review' },
  { key: 'converted', label: 'Converted' },
  { key: 'declined', label: 'Declined' },
  { key: 'spam', label: 'Spam' },
];

function statusBadge(status) {
  const meta = STATUS_META[status] || { label: status, color: 'bg-secondary' };
  return <span className={`badge ${meta.color}`}>{meta.label}</span>;
}

function fileTypeBadge(filename) {
  if (!filename) return { label: '???', color: 'bg-secondary' };
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf': return { label: 'PDF', color: 'bg-danger' };
    case 'dxf':
    case 'dwg': return { label: ext.toUpperCase(), color: 'bg-dark' };
    case 'step':
    case 'stp': return { label: 'STEP', color: 'bg-info text-dark' };
    case 'igs':
    case 'iges': return { label: 'IGES', color: 'bg-info text-dark' };
    case 'png':
    case 'jpg':
    case 'jpeg': return { label: 'IMG', color: 'bg-success' };
    default: return { label: ext.toUpperCase().substring(0, 4), color: 'bg-secondary' };
  }
}

function ageText(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) return `${Math.max(1, Math.floor(ms / 60000))}m`;
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function fmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Resolve a PIN to the worker's name via /auth/pin. Returns null when the
// operator cancels or the PIN is bad (after telling them).
async function resolvePinToName(message) {
  const pin = await pinPrompt(message);
  if (!pin) return null;
  const res = await fetch(`${API_BASE}/auth/pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  if (!res.ok) {
    alert('Invalid PIN.');
    return null;
  }
  const user = await res.json();
  return user.name;
}

export default function QuoteRequestsPage() {
  // -- hooks first, always (Amplify treats rules-of-hooks as a build error)
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // {filename, url, isPdf}
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [searchParams] = useSearchParams();
  const autoSelectedRef = useRef(null);

  const linkedId = searchParams.get('id');

  async function loadRequests() {
    setLoading(true);
    setError(null);
    try {
      const qs = filter === 'active' ? '' : `?status=${filter}`;
      const res = await fetch(`${API_BASE}/quote-requests${qs}`);
      if (!res.ok) throw new Error(`Failed to load quote requests: ${await res.text()}`);
      setRequests(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id) {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/quote-requests/${id}`);
      if (!res.ok) throw new Error(`Failed to load request ${id}: ${await res.text()}`);
      setDetail(await res.json());
    } catch (err) {
      alert(err.message);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  // Initial load + reload when the filter changes; 60s poll (house default).
  useEffect(() => {
    loadRequests();
    const interval = setInterval(loadRequests, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Slack deep link: /quote-requests?id=N auto-selects once the list loads.
  // The requested row may be outside the current filter (e.g. spam) — fall
  // back to loading the detail directly.
  useEffect(() => {
    if (!linkedId) return;
    if (autoSelectedRef.current === linkedId) return;
    autoSelectedRef.current = linkedId;
    setSelectedId(Number(linkedId));
    loadDetail(Number(linkedId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId]);

  useEffect(() => {
    if (selectedId != null) loadDetail(selectedId);
    setPreviewFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((r) =>
      [r.reference, r.contact_name, r.company_name, r.email, r.material, r.quantity, r.notes]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [requests, search]);

  async function refreshAll() {
    await loadRequests();
    if (selectedId != null) await loadDetail(selectedId);
  }

  async function handleSetStatus(newStatus, verb) {
    const name = await resolvePinToName(`Enter your PIN to mark ${detail.reference} ${verb}:`);
    if (!name) return;
    setActionBusy(true);
    try {
      const res = await fetch(`${API_BASE}/quote-requests/${detail.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, reviewed_by: name }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await refreshAll();
    } catch (err) {
      alert(`Status change failed: ${err.message}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function handleDownloadFile(file) {
    try {
      const res = await fetch(
        `${API_BASE}/quote-requests/${detail.id}/files/${file.id}/download-url`
      );
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      // Presigned S3 URL — no API key needed, safe for window.open.
      window.open(data.download_url, '_blank', 'noopener');
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    }
  }

  async function handlePreviewFile(file) {
    try {
      const res = await fetch(
        `${API_BASE}/quote-requests/${detail.id}/files/${file.id}/download-url`
      );
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      const ext = (file.filename.split('.').pop() || '').toLowerCase();
      setPreviewFile({
        filename: file.filename,
        url: data.download_url,
        isPdf: ext === 'pdf',
      });
    } catch (err) {
      alert(`Preview failed: ${err.message}`);
    }
  }

  async function handleDownloadZip() {
    try {
      // Served through the API (needs the X-API-Key the fetch wrapper adds),
      // so fetch a blob and hand it to the browser — window.open would 401.
      const res = await fetch(`${API_BASE}/quote-requests/${detail.id}/files.zip`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detail.reference}_drawings.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      alert(`Zip download failed: ${err.message}`);
    }
  }

  const confirmedFiles = detail
    ? (detail.files || []).filter((f) => f.upload_status === 'confirmed')
    : [];

  return (
    <div className="container-fluid my-4" style={{ maxWidth: 1250 }}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h3 className="mb-0">Website Quote Requests</h3>
          <div className="text-muted" style={{ fontSize: 14 }}>
            Submitted through kancraftmfg.com — respond within one business day (the site promises it).
          </div>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={refreshAll} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="d-flex align-items-center flex-wrap gap-2 mt-3 mb-2">
        <div className="btn-group btn-group-sm" role="group">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`btn ${filter === f.key ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 280 }}
          placeholder="Search name, company, email, material…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-body p-0">
          {filtered.length === 0 && !loading && (
            <div className="p-4 text-muted">No quote requests{search ? ' match the search' : ''}.</div>
          )}
          {filtered.length > 0 && (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 12 }}>Ref</th>
                  <th>Received</th>
                  <th>Contact</th>
                  <th>Quantity</th>
                  <th>Material</th>
                  <th>Files</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    style={{ cursor: 'pointer' }}
                    className={selectedId === r.id ? 'table-active' : ''}
                  >
                    <td style={{ paddingLeft: 12 }} className="font-monospace">{r.reference}</td>
                    <td>
                      {new Date(r.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}
                      <span className="text-muted ms-1">({ageText(r.created_at)})</span>
                    </td>
                    <td>
                      <strong>{r.contact_name}</strong>
                      {r.company_name && <span className="text-muted"> · {r.company_name}</span>}
                    </td>
                    <td>{r.quantity || <span className="text-muted">—</span>}</td>
                    <td>{r.material || <span className="text-muted">—</span>}</td>
                    <td>
                      {r.files_total > 0 ? (
                        <span className={`badge ${r.files_confirmed === r.files_total ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {r.files_confirmed}/{r.files_total}
                        </span>
                      ) : (
                        <span className="text-muted">none</span>
                      )}
                    </td>
                    <td>{statusBadge(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detailLoading && <div className="mt-3">Loading request…</div>}

      {detail && !detailLoading && (
        <div className="card mt-3">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <strong className="font-monospace">{detail.reference}</strong>
              {statusBadge(detail.status)}
              {detail.honeypot_tripped && (
                <span className="badge bg-dark" title="The hidden honeypot field was filled — almost certainly a bot.">
                  honeypot
                </span>
              )}
              <span className="text-muted" style={{ fontSize: 13 }}>
                received {new Date(detail.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
              </span>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {detail.status !== 'converted' && (
                <>
                  {detail.status !== 'in_review' && (
                    <button className="btn btn-sm btn-outline-info" disabled={actionBusy}
                      onClick={() => handleSetStatus('in_review', 'In Review')}>
                      Mark In Review
                    </button>
                  )}
                  <button className="btn btn-sm btn-success" disabled={actionBusy}
                    onClick={() => setShowConvertModal(true)}>
                    Convert to Customer + Quote
                  </button>
                  {detail.status !== 'declined' && (
                    <button className="btn btn-sm btn-outline-secondary" disabled={actionBusy}
                      onClick={() => handleSetStatus('declined', 'Declined')}>
                      Decline
                    </button>
                  )}
                  {detail.status !== 'spam' && (
                    <button className="btn btn-sm btn-outline-dark" disabled={actionBusy}
                      onClick={() => handleSetStatus('spam', 'Spam')}>
                      Mark Spam
                    </button>
                  )}
                  {detail.status !== 'new' && (
                    <button className="btn btn-sm btn-outline-warning" disabled={actionBusy}
                      onClick={() => handleSetStatus('new', 'New')}>
                      Back to New
                    </button>
                  )}
                </>
              )}
              {detail.status === 'converted' && detail.quote_id && (
                <Link className="btn btn-sm btn-primary" to={`/quotes?id=${detail.quote_id}`}>
                  Open Draft Quote #{detail.quote_id} →
                </Link>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <h6 className="text-muted">Contact</h6>
                <div><strong>{detail.contact_name}</strong></div>
                {detail.company_name && <div>{detail.company_name}</div>}
                <div>
                  <a href={`mailto:${detail.email}?subject=Re: your quote request ${detail.reference}`}>
                    {detail.email}
                  </a>
                </div>
                {detail.phone && <div><a href={`tel:${detail.phone}`}>{detail.phone}</a></div>}
                {detail.reviewed_by && (
                  <div className="text-muted mt-2" style={{ fontSize: 13 }}>
                    Last reviewed by {detail.reviewed_by}
                    {detail.reviewed_at &&
                      ` · ${new Date(detail.reviewed_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })}`}
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <h6 className="text-muted">Request</h6>
                <dl className="row mb-0" style={{ fontSize: 14 }}>
                  <dt className="col-4">Quantity</dt>
                  <dd className="col-8">{detail.quantity || '—'}</dd>
                  <dt className="col-4">Material</dt>
                  <dd className="col-8">{detail.material || '—'}</dd>
                  <dt className="col-4">Needed by</dt>
                  <dd className="col-8">{detail.target_date || '—'}</dd>
                </dl>
                {detail.notes && (
                  <div className="mt-2 p-2 bg-light border rounded" style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                    {detail.notes}
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <h6 className="text-muted d-flex justify-content-between align-items-center">
                  <span>Drawings ({confirmedFiles.length})</span>
                  {confirmedFiles.length > 1 && (
                    <button className="btn btn-sm btn-outline-primary" onClick={handleDownloadZip}>
                      Download all (.zip)
                    </button>
                  )}
                </h6>
                {(detail.files || []).length === 0 && (
                  <div className="text-muted" style={{ fontSize: 14 }}>
                    No files attached — the prospect may follow up by email.
                  </div>
                )}
                <ul className="list-group list-group-flush">
                  {(detail.files || []).map((f) => {
                    const badge = fileTypeBadge(f.filename);
                    const previewable = /\.(pdf|png|jpg|jpeg)$/i.test(f.filename);
                    return (
                      <li key={f.id} className="list-group-item d-flex align-items-center gap-2 px-0">
                        <span className={`badge ${badge.color}`}>{badge.label}</span>
                        <span className="flex-grow-1 text-truncate" title={f.filename}>{f.filename}</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>{fmtBytes(f.actual_size_bytes)}</span>
                        {f.upload_status === 'confirmed' ? (
                          <>
                            {previewable && (
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handlePreviewFile(f)}>
                                Preview
                              </button>
                            )}
                            <button className="btn btn-sm btn-outline-primary" onClick={() => handleDownloadFile(f)}>
                              Download
                            </button>
                          </>
                        ) : (
                          <span
                            className={`badge ${f.upload_status === 'pending' ? 'bg-warning text-dark' : 'bg-danger'}`}
                            title="The upload never completed on the customer's side — their drawings may not have arrived. Reply and ask them to email the files."
                          >
                            {f.upload_status}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {previewFile && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong>{previewFile.filename}</strong>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setPreviewFile(null)}>
                    Close preview
                  </button>
                </div>
                {previewFile.isPdf ? (
                  <embed src={previewFile.url} type="application/pdf" width="100%" height="700px" />
                ) : (
                  <img src={previewFile.url} alt={previewFile.filename} style={{ maxWidth: '100%', height: 'auto' }} />
                )}
              </div>
            )}

            <div className="text-muted mt-3" style={{ fontSize: 12 }}>
              Submitted from {detail.submitter_ip || 'unknown IP'}
              {detail.user_agent && ` · ${detail.user_agent.substring(0, 90)}`}
            </div>
          </div>
        </div>
      )}

      {showConvertModal && detail && (
        <ConvertModal
          detail={detail}
          onClose={() => setShowConvertModal(false)}
          onConverted={async () => {
            setShowConvertModal(false);
            await refreshAll();
          }}
        />
      )}
    </div>
  );
}

// Convert a request into a customer + bare Draft quote. Never silently
// dedupes: the API returns 409 with candidates when an existing customer
// shares the request's email, and the operator explicitly picks attach vs
// force-create.
function ConvertModal({ detail, onClose, onConverted }) {
  const [mode, setMode] = useState(detail.customer_matches?.length ? 'existing' : 'new');
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    detail.customer_matches?.[0]?.customer_id || ''
  );
  const [newName, setNewName] = useState(detail.company_name || detail.contact_name || '');
  const [busy, setBusy] = useState(false);
  const [conflictCandidates, setConflictCandidates] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/customers`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCustomerList((data || []).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, []);

  async function submit(forceNew) {
    if (mode === 'existing' && !selectedCustomerId) {
      alert('Pick a customer to attach to.');
      return;
    }
    if (mode === 'new' && !newName.trim()) {
      alert('Enter a name for the new customer.');
      return;
    }
    const name = await resolvePinToName(`Enter your PIN to convert ${detail.reference}:`);
    if (!name) return;
    setBusy(true);
    setConflictCandidates(null);
    try {
      const body = { reviewed_by: name };
      if (mode === 'existing') {
        body.customer_id = Number(selectedCustomerId);
      } else {
        body.new_customer = {
          name: newName.trim(),
          contact_name: detail.contact_name,
          email: detail.email,
          phone: detail.phone,
        };
        if (forceNew) body.force_new = true;
      }
      const res = await fetch(`${API_BASE}/quote-requests/${detail.id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 409) {
        const payload = await res.json();
        const candidates = payload?.detail?.candidates;
        if (candidates?.length) {
          // Existing customer(s) share this email — make the choice explicit.
          setConflictCandidates(candidates);
          return;
        }
        throw new Error(payload?.detail?.message || detailToString(payload?.detail) || 'Conflict');
      }
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const result = await res.json();
      alert(
        `Converted ${detail.reference}: ` +
        `${result.customer_created ? 'created customer' : 'attached to customer'} #${result.customer_id}, ` +
        `Draft Quote #${result.quote_id}. Opening the Quotes page will show it.`
      );
      await onConverted(result);
    } catch (err) {
      alert(`Convert failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Convert {detail.reference}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted" style={{ fontSize: 14 }}>
              Creates a bare Draft quote (you add the lines in the Quotes page) and links it
              back to this request. The drawings stay here on the request.
            </p>

            {detail.customer_matches?.length > 0 && (
              <div className="alert alert-info py-2" style={{ fontSize: 14 }}>
                Possible existing match{detail.customer_matches.length > 1 ? 'es' : ''}:{' '}
                {detail.customer_matches.map((m) => `${m.name} (#${m.customer_id})`).join(', ')}
              </div>
            )}

            <div className="form-check">
              <input className="form-check-input" type="radio" id="qr-conv-existing"
                checked={mode === 'existing'} onChange={() => setMode('existing')} />
              <label className="form-check-label" htmlFor="qr-conv-existing">
                Attach to an existing customer
              </label>
            </div>
            {mode === 'existing' && (
              <select className="form-select my-2" value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}>
                <option value="">-- Select customer --</option>
                {customerList.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name} (#{c.customer_id})
                  </option>
                ))}
              </select>
            )}

            <div className="form-check mt-2">
              <input className="form-check-input" type="radio" id="qr-conv-new"
                checked={mode === 'new'} onChange={() => setMode('new')} />
              <label className="form-check-label" htmlFor="qr-conv-new">
                Create a new customer
              </label>
            </div>
            {mode === 'new' && (
              <div className="my-2">
                <label className="form-label mb-1">Customer name</label>
                <input type="text" className="form-control" value={newName}
                  onChange={(e) => setNewName(e.target.value)} />
                <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                  Contact, email and phone are copied from the request:
                  {' '}{detail.contact_name} · {detail.email}{detail.phone ? ` · ${detail.phone}` : ''}
                </div>
              </div>
            )}

            {conflictCandidates && (
              <div className="alert alert-warning mt-3" style={{ fontSize: 14 }}>
                <strong>Existing customer(s) already use {detail.email}:</strong>
                <ul className="mb-2">
                  {conflictCandidates.map((c) => (
                    <li key={c.customer_id}>
                      {c.name} (#{c.customer_id}){c.contact_name ? ` — ${c.contact_name}` : ''}
                    </li>
                  ))}
                </ul>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-primary" disabled={busy}
                    onClick={() => {
                      setMode('existing');
                      setSelectedCustomerId(conflictCandidates[0].customer_id);
                      setConflictCandidates(null);
                    }}>
                    Attach to {conflictCandidates[0].name}
                  </button>
                  <button className="btn btn-sm btn-outline-danger" disabled={busy}
                    onClick={() => submit(true)}>
                    Create new customer anyway
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn btn-success" onClick={() => submit(false)} disabled={busy}>
              {busy ? 'Converting…' : 'Convert'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
